import type { ReactNode, RefObject } from "react";
import { useEffect, useRef } from "react";

import isEmpty from "lodash/isEmpty";

import { client as launchDarklyClient } from "@/crossPlatform/LaunchDarkly";
import {
  isPollingMode,
  LaunchDarklyConfig,
  LaunchDarklyConnectionMode,
} from "@/crossPlatform/LaunchDarkly/config";
import type { LaunchDarklyUserMultiContext } from "@/crossPlatform/LaunchDarkly/types";
import useAppForegroundState from "@/hooks/useAppForegroundState";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import useAppMonitor from "@/services/AppMonitor";
import { useAppUserSelector } from "@/store/reducers/appUser";

interface LaunchDarklyIdentifyProviderProps {
  children: ReactNode;
}

const LaunchDarklyIdentifyProvider = ({
  children,
  // eslint-disable-next-line sonarjs/function-return-type -- currently reported in SonarQube as unresolved
}: LaunchDarklyIdentifyProviderProps) => {
  const { externalId: appUserExternalId, isPatient } = useAppUserSelector();
  const { captureException } = useAppMonitor();

  const { getDeviceInfo } = useDeviceInfo();

  // Toggle LaunchDarkly offline in background; resume on foreground
  const onChangeRef: RefObject<
    ({
      currentState,
      nextState,
    }: {
      currentState: string;
      nextState: string;
    }) => void
  > = useRef(({ nextState }) => {
    const isBecomingActive = nextState === "active";

    if (!isBecomingActive) {
      void launchDarklyClient.setConnectionMode("offline");
      return;
    }

    const mode = isPollingMode()
      ? LaunchDarklyConnectionMode.polling
      : LaunchDarklyConnectionMode.streaming;

    void launchDarklyClient.setConnectionMode(mode);
  });

  useAppForegroundState({ onChangeRef });

  useEffect(() => {
    const updateLaunchDarklyUserContext = async () => {
      if (isEmpty(appUserExternalId)) return;

      const deviceInfo = await getDeviceInfo();

      const updatedContext: LaunchDarklyUserMultiContext = {
        device: {
          appVersion: deviceInfo.appVersion,
          key: deviceInfo.deviceId,
          manufacturer: deviceInfo.manufacturer,
          modelName: deviceInfo.modelName,
          osName: deviceInfo.osName,
          osVersion: deviceInfo.osVersion,
        },
        kind: "multi",
        organization: {
          key: LaunchDarklyConfig.Key,
        },
        user: {
          key: appUserExternalId,
          role: isPatient ? "Patient" : "Support",
        },
      };

      launchDarklyClient
        .identify(updatedContext)
        .catch((caughtErr: Error) => captureException(caughtErr));
    };

    void updateLaunchDarklyUserContext();
  }, [appUserExternalId, captureException, getDeviceInfo, isPatient]);

  return children;
};

export default LaunchDarklyIdentifyProvider;
